import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2, Hotel, Bus, Plane, Users, Search, Layers } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { format } from 'date-fns';

type AllocationType = 'HOTEL_ROOM' | 'TOUR_SEAT' | 'TRANSFER_VEHICLE' | 'FLIGHT_SEAT';

interface BookingItemFormData {
  serviceType: string;
  name: string;
  description?: string;
  serviceDate?: string;
  endDate?: string;
  quantity: number;
  unitCost: number;
  sellingPrice: number;
  currency: string;
  partnerId?: string;
  ticketNumber?: string;
  pnr?: string;
  returnPnr?: string;
  notes?: string;
}

interface BookingItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  defaultServiceDate?: Date;
  defaultEndDate?: Date;
  onSuccess?: () => void;
}

const serviceTypes = [
  { value: 'ACCOMMODATION', label: 'Accommodation' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'ACTIVITY', label: 'Activity' },
  { value: 'MEAL', label: 'Meal' },
  { value: 'GUIDE', label: 'Guide' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'OTHER', label: 'Other' },
];

const typeIcons: Record<AllocationType, typeof Hotel> = {
  HOTEL_ROOM: Hotel,
  TOUR_SEAT: Users,
  TRANSFER_VEHICLE: Bus,
  FLIGHT_SEAT: Plane,
};

const typeColors: Record<AllocationType, string> = {
  HOTEL_ROOM: 'bg-blue-100 text-blue-700',
  TOUR_SEAT: 'bg-purple-100 text-purple-700',
  TRANSFER_VEHICLE: 'bg-green-100 text-green-700',
  FLIGHT_SEAT: 'bg-sky-100 text-sky-700',
};

export function BookingItemModal({
  open,
  onOpenChange,
  bookingId,
  defaultServiceDate,
  defaultEndDate,
  onSuccess,
}: BookingItemModalProps) {
  const { t } = useTranslation('bookings');
  const { t: tAllocations } = useTranslation('allocations');
  const { t: tCommon } = useTranslation('common');

  const [useAllocation, setUseAllocation] = useState(false);
  const [allocationSearch, setAllocationSearch] = useState('');
  const [selectedAllocation, setSelectedAllocation] = useState<string | null>(null);
  const [allocationUnits, setAllocationUnits] = useState(1);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<BookingItemFormData>({
    defaultValues: {
      serviceType: 'ACCOMMODATION',
      quantity: 1,
      unitCost: 0,
      sellingPrice: 0,
      currency: 'USD',
      serviceDate: defaultServiceDate
        ? format(defaultServiceDate, 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'),
      endDate: defaultEndDate ? format(defaultEndDate, 'yyyy-MM-dd') : '',
    },
  });

  const watchedServiceType = watch('serviceType');
  const watchedServiceDate = watch('serviceDate');
  const watchedQuantity = watch('quantity') || 1;
  const watchedUnitCost = watch('unitCost') || 0;
  const isFlightType = watchedServiceType === 'TRANSPORT';

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      reset({
        serviceType: 'ACCOMMODATION',
        quantity: 1,
        unitCost: 0,
        sellingPrice: 0,
        currency: 'USD',
        serviceDate: defaultServiceDate
          ? format(defaultServiceDate, 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd'),
        endDate: defaultEndDate ? format(defaultEndDate, 'yyyy-MM-dd') : '',
      });
      setUseAllocation(false);
      setSelectedAllocation(null);
      setAllocationUnits(1);
      setAllocationSearch('');
    }
  }, [open, defaultServiceDate, defaultEndDate, reset]);

  // Query partners for selection
  const { data: partners } = trpc.partner.list.useQuery({
    page: 1,
  });

  // Query available allocations
  const { data: allocationsData, isLoading: allocationsLoading } =
    trpc.allocation.list.useQuery(
      {
        page: 1,
        search: allocationSearch || undefined,
        status: 'ACTIVE',
        hasAvailability: true,
        validFrom: watchedServiceDate ? new Date(watchedServiceDate) : new Date(),
      },
      {
        enabled: open && useAllocation,
      }
    );

  const utils = trpc.useUtils();

  const addItemMutation = trpc.booking.addItem.useMutation({
    onSuccess: () => {
      showSuccessToast(t('items.added') || 'Item added successfully');
      utils.booking.byId.invalidate(bookingId);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const onSubmit = (data: BookingItemFormData) => {
    addItemMutation.mutate({
      bookingId,
      serviceType: data.serviceType as
        | 'ACCOMMODATION'
        | 'TRANSPORT'
        | 'ACTIVITY'
        | 'MEAL'
        | 'GUIDE'
        | 'INSURANCE'
        | 'TRANSFER'
        | 'OTHER',
      name: data.name,
      description: data.description,
      serviceDate: data.serviceDate ? new Date(data.serviceDate) : undefined,
      endDate: data.endDate ? new Date(data.endDate) : undefined,
      quantity: data.quantity,
      unitCost: data.unitCost,
      sellingPrice: data.sellingPrice,
      currency: data.currency,
      partnerId: data.partnerId || undefined,
      ticketNumber: data.ticketNumber || undefined,
      pnr: data.pnr || undefined,
      returnPnr: data.returnPnr || undefined,
      notes: data.notes,
      allocationId: useAllocation && selectedAllocation ? selectedAllocation : undefined,
      allocationUnits: useAllocation && selectedAllocation ? allocationUnits : undefined,
    });
  };

  const allocations = allocationsData?.allocations ?? [];
  const selectedAllocationData = allocations.find((a) => a.id === selectedAllocation);

  // Auto-fill cost from allocation
  useEffect(() => {
    if (selectedAllocationData) {
      setValue('unitCost', selectedAllocationData.unitCost);
      setValue('currency', selectedAllocationData.currency);
      // Set selling price with a default markup (e.g., 20%)
      setValue('sellingPrice', Math.round(selectedAllocationData.unitCost * 1.2 * 100) / 100);
    }
  }, [selectedAllocationData, setValue]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('items.addItem') || 'Add Booking Item'}</DialogTitle>
          <DialogDescription>
            {t('items.addItemDescription') ||
              'Add a service item to this booking with optional supplier allocation.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Service Type and Name */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t('items.serviceType') || 'Service Type'} *</Label>
              <Combobox
                options={serviceTypes.map((type) => ({
                  value: type.value,
                  label: type.label,
                }))}
                value={watch('serviceType')}
                onValueChange={(value) => setValue('serviceType', value)}
                placeholder="Select service type"
                searchPlaceholder="Search..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t('items.name') || 'Item Name'} *</Label>
              <Input
                id="name"
                {...register('name', { required: 'Name is required' })}
                placeholder="e.g., Hilton Istanbul - Deluxe Room"
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('items.description') || 'Description'}</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Additional details..."
              rows={2}
            />
          </div>

          {/* Dates */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="serviceDate">{t('items.serviceDate') || 'Service Date'}</Label>
              <Input id="serviceDate" type="date" {...register('serviceDate')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">{t('items.endDate') || 'End Date'}</Label>
              <Input id="endDate" type="date" {...register('endDate')} />
            </div>
          </div>

          {/* Flight-specific fields */}
          {isFlightType && (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="ticketNumber">{t('items.ticketNumber') || 'Ticket Number'} *</Label>
                <Input
                  id="ticketNumber"
                  {...register('ticketNumber', {
                    required: isFlightType ? (t('items.ticketNumberRequired') || 'Ticket number is required for flights') : false,
                  })}
                  placeholder="e.g., 235-1234567890"
                />
                {errors.ticketNumber && (
                  <p className="text-sm text-red-500">{errors.ticketNumber.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="pnr">{t('items.pnr') || 'PNR'}</Label>
                <Input
                  id="pnr"
                  {...register('pnr')}
                  placeholder="e.g., ABC123"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="returnPnr">{t('items.returnPnr') || 'Return PNR'}</Label>
                <Input
                  id="returnPnr"
                  {...register('returnPnr')}
                  placeholder="e.g., DEF456"
                />
              </div>
            </div>
          )}

          {/* Partner Selection */}
          <div className="space-y-2">
            <Label>{t('items.partner') || 'Supplier/Partner'}</Label>
            <Combobox
              options={[
                { value: '_none', label: 'No specific partner' },
                ...(partners?.partners.map((partner) => ({
                  value: partner.id,
                  label: partner.companyName,
                })) ?? []),
              ]}
              value={watch('partnerId') || '_none'}
              onValueChange={(value) =>
                setValue('partnerId', value === '_none' ? undefined : value)
              }
              placeholder="Select partner (optional)"
              searchPlaceholder="Search partners..."
            />
          </div>

          {/* Allocation Selection */}
          <div className="space-y-4 rounded-lg border p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                id="useAllocation"
                checked={useAllocation}
                onChange={(e) => {
                  setUseAllocation(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedAllocation(null);
                  }
                }}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {t('items.useAllocation') || 'Use Supplier Allocation'}
                </span>
              </div>
            </label>

            {useAllocation && (
              <div className="space-y-4 pt-2">
                {/* Allocation Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={tAllocations('searchPlaceholder') || 'Search allocations...'}
                    value={allocationSearch}
                    onChange={(e) => setAllocationSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Allocations List */}
                {allocationsLoading && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                )}

                {!allocationsLoading && allocations.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {tAllocations('empty.noAllocations') || 'No available allocations found'}
                  </p>
                )}

                {!allocationsLoading && allocations.length > 0 && (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {allocations.map((allocation) => {
                      const Icon = typeIcons[allocation.allocationType as AllocationType];
                      const isSelected = selectedAllocation === allocation.id;

                      return (
                        <div
                          key={allocation.id}
                          onClick={() => setSelectedAllocation(allocation.id)}
                          className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'hover:bg-muted/50'
                          }`}
                        >
                          <div
                            className={`flex h-8 w-8 items-center justify-center rounded ${
                              typeColors[allocation.allocationType as AllocationType]
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{allocation.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {allocation.partner.companyName}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-green-600">
                              {allocation.availableUnits}{' '}
                              {tAllocations('fields.available') || 'available'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: allocation.currency,
                              }).format(allocation.unitCost)}
                              /unit
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Allocation Units */}
                {selectedAllocationData && (
                  <div className="rounded-lg bg-muted/50 p-3 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {tAllocations('fields.selected') || 'Selected'}:
                      </span>
                      <span className="font-medium">{selectedAllocationData.name}</span>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="allocationUnits">
                        {tAllocations('fields.units') || 'Units to consume'}
                      </Label>
                      <Input
                        id="allocationUnits"
                        type="number"
                        min={1}
                        max={selectedAllocationData.availableUnits}
                        value={allocationUnits}
                        onChange={(e) =>
                          setAllocationUnits(
                            Math.min(
                              Number(e.target.value),
                              selectedAllocationData.availableUnits
                            )
                          )
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {tAllocations('fields.totalCost') || 'Allocation Cost'}:
                      </span>
                      <span className="font-medium">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: selectedAllocationData.currency,
                        }).format(selectedAllocationData.unitCost * allocationUnits)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pricing */}
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">{t('items.quantity') || 'Quantity'} *</Label>
              <Input
                id="quantity"
                type="number"
                min={1}
                {...register('quantity', { valueAsNumber: true, min: 1 })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unitCost">{t('items.unitCost') || 'Unit Cost'} *</Label>
              <Input
                id="unitCost"
                type="number"
                step="0.01"
                min={0}
                {...register('unitCost', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sellingPrice">{t('items.sellingPrice') || 'Selling Price'} *</Label>
              <Input
                id="sellingPrice"
                type="number"
                step="0.01"
                min={0}
                {...register('sellingPrice', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('items.currency') || 'Currency'}</Label>
              <Combobox
                options={[
                  { value: 'USD', label: 'USD' },
                  { value: 'EUR', label: 'EUR' },
                  { value: 'GBP', label: 'GBP' },
                  { value: 'TRY', label: 'TRY' },
                ]}
                value={watch('currency')}
                onValueChange={(value) => setValue('currency', value)}
                placeholder="Select currency"
                searchPlaceholder="Search..."
              />
            </div>
          </div>

          {/* Summary */}
          <div className="rounded-lg bg-muted p-4">
            <h4 className="font-medium mb-2">{t('items.summary') || 'Item Summary'}</h4>
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('items.totalCost') || 'Total Cost'}
                </span>
                <span className="font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: watch('currency') || 'USD',
                  }).format(watchedUnitCost * watchedQuantity)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('items.totalSellingPrice') || 'Total Selling Price'}
                </span>
                <span className="font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: watch('currency') || 'USD',
                  }).format((watch('sellingPrice') || 0) * watchedQuantity)}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('items.notes') || 'Notes'}</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Internal notes..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={addItemMutation.isPending}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={addItemMutation.isPending}>
              {addItemMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t('items.addItem') || 'Add Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
